import axios from "axios";
import { API_ROUTES, buildExtraInfo, encryptData, decryptResponseMessages, postEncryptedJson } from "../../utils/apiClient";
import { hashPassword } from "../../utils/passwordHasher";

/**
 * Helper to get the auth token from storage.
 * Returns null if token is missing or FIRST_TIME_LOGIN.
 */
const getAuthToken = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    return null;
  }
  return token;
};

/**
 * Build full URL for demo deposit operations.
 * If options.baseURL is provided, it will be used instead of the default API route.
 */
const getDepositDemoUrl = (options = {}) => {
  const baseURL = options.baseURL;
  if (baseURL && typeof baseURL === "string" && baseURL.trim() !== "") {
    const normalized = baseURL.replace(/\/+$/, "");
    return `${normalized}/v1/user/create-deposit-demo-operation`;
  }
  return API_ROUTES.CREATE_DEPOSIT_DEMO_OPERATION;
};

/**
 * Create a DEMO deposit operation for a given wallet.
 *
 * API: /v1/user/create-deposit-demo-operation
 * BODY:
 * {
 *   "MESSAGES": [encrypted_segment1, encrypted_segment2, ...]
 * }
 *
 * Where encrypted data contains:
 * {
 *   "HEADERS": {{extra_infos}},
 *   "BODY": {
 *     "AMOUNT": number,
 *     "WALLET_ID": number
 *   }
 * }
 *
 * - HEADERS are built via apiClient.buildExtraInfo()
 * - Data is encrypted using RSA encryption
 * - Auth token is sent in main headers as Bearer token
 * - Device headers are also included
 *
 * @param {number} amount
 * @param {number} walletId
 * @param {object} options Optional options: { baseURL?: string }
 * @returns {Promise<any>} Response with decryptedData property
 */
export const createDepositDemoOperation = async (amount, walletId, options = {}) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication token is required. Please login first.");
  }

  const extraInfo = await buildExtraInfo();
  // Ensure amount is a number (not string)
  const amountNum = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(amountNum) || amountNum <= 0) {
    throw new Error("AMOUNT must be a positive number.");
  }
  // Ensure walletId is a number
  const walletIdNum = typeof walletId === 'string' ? parseInt(walletId, 10) : Number(walletId);
  if (isNaN(walletIdNum)) {
    throw new Error("WALLET_ID must be a valid number.");
  }
  
  const body = {
    AMOUNT: amountNum,
    WALLET_ID: walletIdNum,
  };
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };

  const url = getDepositDemoUrl(options);
  const encryptedMessages = await encryptData(dataToEncrypt);
  
  // Use JSON format like createInternalTransferMoneyOperation (no file uploads)
  const requestBody = {
    MESSAGES: Array.isArray(encryptedMessages) ? encryptedMessages : [encryptedMessages],
  };

  const headers = {
    'Content-Type': 'application/json; charset=UTF-8',
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position": extraInfo["device-position"] ?? JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };

  let response;
  try {
    response = await axios.post(url, requestBody, { headers });
  } catch (e) {
    console.error('Demo deposit error:', e);
    console.error('Demo deposit error response:', e.response?.data);
    console.error('Demo deposit error status:', e.response?.status);
    // If it's an HTTP error response, try to decrypt and include it
    if (e.response) {
      response = e.response;
      const decryptedError = await decryptResponseMessages(response.data);
      console.error('Demo deposit decrypted error:', decryptedError);
      return { ...response, decryptedData: decryptedError || response.data };
    }
    // If it's a network error or other error, throw it
    throw e;
  }

  // Decrypt response using the helper from apiClient
  const decryptedResponse = await decryptResponseMessages(response.data);
  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * Apply a DEMO deposit operation by providing the transaction ID and password.
 *
 * API: /v1/user/apply-deposit-demo-operation
 * BODY:
 * {
 *   "MESSAGES": [encrypted_segment1, encrypted_segment2, ...]
 * }
 *
 * Where encrypted data contains:
 * {
 *   "HEADERS": {{extra_infos}},
 *   "BODY": {
 *     "PASSWORD": "<hashed password>",
 *     "TRANSACTION_ID": "<transaction id from create step>"
 *   }
 * }
 *
 * - PASSWORD is hashed on the client using the same helper as cash-in/cash-out.
 * - HEADERS are built via apiClient.buildExtraInfo()
 * - Data is encrypted using RSA encryption
 * - Auth token is sent in main headers as Bearer token
 * - Device headers are also included
 * - Uses postEncryptedJson helper like other apply operations (applyInternalTransferMoneyOperation, etc.)
 *
 * @param {string} transactionId
 * @param {string} plainPassword PIN / password entered by the user (will be hashed)
 * @param {object} options Optional options: { baseURL?: string }
 * @returns {Promise<any>} Response with decryptedData property
 */
export const applyDepositDemoOperation = async (
  transactionId,
  plainPassword,
  options = {}
) => {
  if (!transactionId) {
    throw new Error("TRANSACTION_ID is required to apply demo deposit operation.");
  }

  const password = (plainPassword || "").trim();
  if (!password) {
    throw new Error("Password is required to apply demo deposit operation.");
  }

  const hashedPassword = await hashPassword(password);
  // Use TRANSACTION_ID (like applyCashInMoneyOperation and applyInternalTransferMoneyOperation)
  const body = {
    PASSWORD: hashedPassword,
    TRANSACTION_ID: transactionId,
  };

  // Use postEncryptedJson helper like other apply operations
  return postEncryptedJson(API_ROUTES.APPLY_DEPOSIT_DEMO_OPERATION, body, options);
};

export default {
  createDepositDemoOperation,
  applyDepositDemoOperation,
};

